import React, { useState, useEffect } from 'react';
import { X, Download, FileText, Image as ImageIcon, File } from 'lucide-react';

const FileViewer = ({ fileUrl, fileName, onClose }) => {
    const [fileType, setFileType] = useState('');

    useEffect(() => {
        if (fileUrl) {
            const extension = fileUrl.split('.').pop().toLowerCase().split('?')[0];
            if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(extension)) {
                setFileType('image');
            } else if (extension === 'pdf') {
                setFileType('pdf');
            } else {
                setFileType('other');
            }
        }
    }, [fileUrl]);

    const handleDownload = async () => {
        try {
            const response = await fetch(fileUrl);
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = fileName || 'download';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Download failed:', error);
            // Fallback to simple open in new tab if fetch fails (e.g. CORS)
            window.open(fileUrl, '_blank');
        }
    };

    if (!fileUrl) return null;

    return (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
            <div className="relative w-full max-w-5xl h-[85vh] bg-white rounded-xl shadow-2xl flex flex-col overflow-hidden">
                {/* Header */}
                <div className="flex items-center justify-between px-4 py-3 border-b bg-gray-50">
                    <div className="flex items-center space-x-3 overflow-hidden">
                        <div className="p-2 bg-blue-100 rounded-lg">
                            {fileType === 'image' ? (
                                <ImageIcon className="w-5 h-5 text-blue-600" />
                            ) : fileType === 'pdf' ? (
                                <FileText className="w-5 h-5 text-red-600" />
                            ) : (
                                <File className="w-5 h-5 text-gray-600" />
                            )}
                        </div>
                        <span className="font-medium text-gray-900 truncate max-w-md" title={fileName}>
                            {fileName}
                        </span>
                    </div>

                    <div className="flex items-center space-x-2">
                        <button
                            onClick={handleDownload}
                            className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title="Download"
                        >
                            <Download className="w-5 h-5" />
                        </button>
                        <button
                            onClick={onClose}
                            className="p-2 text-gray-500 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                            title="Close"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>
                </div>

                {/* Content */}
                <div className="flex-1 bg-gray-100 flex items-center justify-center overflow-hidden relative">
                    {fileType === 'image' ? (
                        <img
                            src={fileUrl}
                            alt={fileName}
                            className="max-w-full max-h-full object-contain"
                        />
                    ) : fileType === 'pdf' ? (
                        <iframe
                            src={fileUrl}
                            title={fileName}
                            className="w-full h-full border-0"
                        />
                    ) : (
                        <div className="text-center p-8">
                            <div className="w-24 h-24 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
                                <File className="w-12 h-12 text-gray-400" />
                            </div>
                            <h3 className="text-lg font-medium text-gray-900 mb-2">Preview not available</h3>
                            <p className="text-gray-500 mb-6">This file type cannot be previewed directly.</p>
                            <button
                                onClick={handleDownload}
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center mx-auto space-x-2"
                            >
                                <Download className="w-4 h-4" />
                                <span>Download File</span>
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* Backdrop click to close */}
            <div className="absolute inset-0 -z-10" onClick={onClose} />
        </div>
    );
};

export default FileViewer;
